import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/membershipType/utils/constants';
import { usePatchMembershipType } from 'src/features/membershipType/api/usePatchMembershipType';
import { AllowedTimes } from 'src/features/membershipType/types';
import { LIST_ALLOWED_TIMES_RESOURCE } from 'src/features/membershipType/api/allowedTime/useListMembershipTypeAllowedTimes';
import AllowedTimeFormModal from 'src/features/membershipType/components/forms/allowedTime/AllowedTimeFormModal';
import { allowedTimesMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';

interface UpdateAllowedTimeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowedTime?: AllowedTimes;
    allowedTimesData?: AllowedTimes[];
    children?: React.ReactNode;
}

function UpdateAllowedTimeFormModal<TData>({
    className,
    children,
    selectedAllowedTime,
    allowedTimesData,
}: UpdateAllowedTimeFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const dayOfWeek = selectedAllowedTime?.day_of_week;

    const { mutate: updateAllowedTime, isPending } = usePatchMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOWED_TIMES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            toast({
                title: formatMessage(allowedTimesMessages.updateAllowedTimeSuccess),
                description: formatMessage(allowedTimesMessages.updateAllowedTimeSuccessDescription, {
                    entry: dayOfWeek && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[dayOfWeek]),
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <AllowedTimeFormModal
            selectedAllowedTime={selectedAllowedTime}
            allowedTimes={allowedTimesData}
            className={className}
            mutate={(data, onSuccess, onError) =>
                updateAllowedTime(
                    { parkingId, id: membershipTypeId, membership_type_allowed_times: data },
                    { onSuccess, onError },
                )
            }
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(allowedTimesMessages.allowedTime).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(allowedTimesMessages.editAllowedTimeSubtitle, {
                entry: selectedAllowedTime?.day_of_week
                    ? formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[selectedAllowedTime?.day_of_week]).toLowerCase()
                    : '',
            })}
        >
            {children}
        </AllowedTimeFormModal>
    );
}

export default UpdateAllowedTimeFormModal;
